import type { JSX } from 'react';
import type { DogFlag } from '../../lib/api.js';
import { DogFlagChips } from '../dog-first/index.js';
import {
  buildStayColumns,
  paginateColumns,
  buildGridRows,
  formatAge,
  formatSexAltered,
  formatStayWindow,
  formatFeeding,
  formatGingrImportCaption,
  formatAllergies,
  type RunCardFeeding,
  type RunCardMedication,
} from './run-card-grid.js';

/**
 * Run card v2 (#549). Data for one printed card — one boarding/daycare
 * dog. Decoupled from the API shape (`RunCardApiCard` in lib/api.ts): the
 * page filling the deck maps API rows to this and hands it over. All
 * display derivation (age, sex/altered, stay window, day-grid columns and
 * rows) is done by the pure `run-card-grid` helpers so it stays UTC-safe
 * and unit-testable.
 */
export interface RunCardData {
  /** Unique per card (`${bookingId}:${dogId}`) — testid + React key root. */
  readonly cardId: string;
  readonly bookingId: string;
  readonly dogId: string;
  readonly dogName: string;
  readonly ownerName: string;
  readonly ownerPhone: string | null;
  readonly photoUrl: string | null;
  readonly breed: string | null;
  /** Birth date ISO (UTC midnight) — age is derived, never precomputed. */
  readonly birthDateISO: string | null;
  readonly sex: string | null;
  readonly alteredStatus: string | null;
  readonly isNeutered: boolean | null;
  readonly temperament: string | null;
  /** Service display name, e.g. "Boarding". */
  readonly serviceLabel: string;
  /** 1-based position + count when the booking covers multiple dogs. */
  readonly dogIndex: number;
  readonly dogCount: number;
  /** Stay window (UTC-midnight ISO). Daycare: start == end. */
  readonly stayStartISO: string | null;
  readonly stayEndISO: string | null;
  readonly dropOffTime: string | null;
  readonly pickUpTime: string | null;
  /**
   * Sawyer 06 beds-lockers-runcard (B5) — where the dog sleeps on the
   * printed night and where its things went; printed big in the header on
   * every page. Absent (an older server) or null ⇒ a labelled blank box.
   */
  readonly placement?: RunCardPlacement;
  readonly feeding: RunCardFeeding;
  readonly medications: ReadonlyArray<RunCardMedication>;
  /** Free-text med instructions (Dog.medicationNotes) — surfaced in the red flag. */
  readonly medicationNotesText: string | null;
  /**
   * #716 — what this dog arrived with, parsed server-side. EMPTY (or
   * omitted, for a server that predates #716) prints the blank-line
   * stationery fallback below.
   */
  readonly belongings?: ReadonlyArray<string>;
  /**
   * #716 — this stay's feeding instructions, captured at check-in. Printed
   * INSTEAD of the profile summary when present.
   */
  readonly feedingOverride?: string | null;
  /**
   * #716 — ISO timestamp of the check-in confirmation, or null. Only a real
   * confirmation prints a "verified" line: copied-forward data is useful but
   * unconfirmed, and the card must not claim otherwise.
   */
  readonly careVerifiedAtISO?: string | null;
  /**
   * Ted #28 Phase 2 (#1050) — the registry's flags for this dog on this stay
   * (INTACT / vax / meds / transport), printed under the name by the ONE
   * renderer. Absent on a server older than Phase 2 ⇒ nothing printed.
   */
  readonly flags?: ReadonlyArray<DogFlag>;
  /** #597 / B7 — the dog's allergies; printed beside feeding when present. */
  readonly allergies?: string | null;
  /**
   * #597 / B7 — import times for the medication / feeding notes when they
   * came from the Gingr mirror (null when typed in ALA). Absent ⇒ no caption.
   */
  readonly gingrCare?: {
    readonly medicationImportedAtISO: string | null;
    readonly feedingImportedAtISO: string | null;
  };
  /**
   * Ted #46 (#1218) — the dog's behavior notes (profile, or this stay's
   * edit). Absent on an older server ⇒ only the temperament prints.
   */
  readonly behaviorNotes?: string | null;
  /** Ted #46 — what the desk added to THIS stay's card at check-in. */
  readonly stayEdits?: RunCardStayEdits;
  /**
   * Ted #46 — set when this card is the visit's printed record rather than
   * today's data. Printed on the card, so a reprint is honest on paper.
   */
  readonly visitRecord?: RunCardVisitRecord | null;
}

/** Sawyer 06 beds-lockers-runcard (B5). */
export interface RunCardPlacement {
  readonly bedName: string | null;
  readonly locker: string | null;
}

/**
 * Over these many characters a placement value steps down a size, so a
 * locker up to 40 characters ("Box A, top shelf, owner leash + food bin")
 * prints IN FULL in two lines, never clipped or ellipsised (principle 5).
 */
export const PLACEMENT_LONG_AT = 8;
export const PLACEMENT_XLONG_AT = 20;

function placementSizeClass(v: string): string {
  if (v.length > PLACEMENT_XLONG_AT) return ' ferro-run-card__placement-value--xlong';
  if (v.length > PLACEMENT_LONG_AT) return ' ferro-run-card__placement-value--long';
  return '';
}

/**
 * Sawyer 06 beds-lockers-runcard (B5) — Sawyer, memo2 [03:00]: "Locker
 * number, bed number ... bigger ... It's really big up top ... People gotta
 * know where to go." One box per fact, the same size whether filled or not:
 * a blank box with a write-in line, never a dash, a guess or a stale value.
 */
function PlacementBox({ label, value, testId }: { label: string; value: string | null; testId: string }): JSX.Element {
  const v = nonEmpty(value);
  return (
    <div className="ferro-run-card__placement-box" data-testid={testId} data-filled={v ? 'true' : 'false'}>
      <span className="ferro-run-card__placement-label">{label}</span>
      {v ? (
        <span className={`ferro-run-card__placement-value${placementSizeClass(v)}`}>
          {v}
        </span>
      ) : (
        <span className="ferro-run-card__placement-blank" aria-label={`${label} not set — write it in`} />
      )}
    </div>
  );
}

export interface RunCardStayEdits {
  readonly note: string | null;
  readonly fields: ReadonlyArray<{ readonly label: string; readonly value: string }>;
  /** Care values that came from this stay, not the profile (tagged on the card). */
  readonly overridden: ReadonlyArray<'allergies' | 'medications' | 'behavior'>;
}

export interface RunCardVisitRecord {
  readonly printedAtISO: string;
  /** Today's profile would print something different. */
  readonly changedSincePrint: boolean;
}

interface RunCardProps {
  readonly data: RunCardData;
}


/**
 * EMPTY-STATE fallback only (#716). Until #716 this hardcoded list WAS the
 * belongings block — nothing about belongings persisted anywhere, so every
 * card printed the same four prompts.
 *
 * It is now what prints when we have nothing on file for this dog: a
 * first-time boarder, or a tenant that has never opened the capture sheet.
 * That is not a compromise between two asks — it is both of them. Ted and
 * Julian asked for BLANK printed cards; Kylie asked for PRE-FILLED ones.
 * "Pre-filled when data exists, blank lines otherwise" is one rule that
 * satisfies each, and it is the line flagged for their confirmation.
 *
 * Do not delete this when the data path feels reliable. A dog with no
 * history still needs prompts, and a blank sheet teaches nobody what to
 * look for at drop-off.
 */
const BELONGINGS_PROMPTS = [
  'Blanket / bed',
  'Toys',
  'Wet food',
  'Walking requests',
] as const;

/** Two hand-write lines follow the list in BOTH states — "the locker would
 * change" (Kylie), and anything unexpected still needs somewhere to go. */
const WRITE_IN_LINES = [0, 1] as const;

/**
 * "Aug 7" for the verification and printed stamps, or null. UTC-based like
 * the rest of the card's date handling (run-card-grid.ts) so a card printed
 * at 11pm never shows tomorrow.
 */
function formatStampDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function initial(name: string): string {
  const c = name.trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

function nonEmpty(text: string | null | undefined): string | null {
  if (!text) return null;
  const t = text.trim();
  return t.length > 0 ? t : null;
}

/** "(this stay)" after a value the desk changed for this visit only. */
function StayTag(): JSX.Element {
  return <span className="ferro-run-card__stay-tag"> (this stay)</span>;
}

/**
 * One printable run card. For a stay longer than 7 days the grid paginates
 * (Gingr's "Run Card N of M"): each page is its own `.ferro-run-card`
 * article (one physical sheet).
 *
 * Ted #45 (#1217), run card v2 — a hospital chart. The SAFETY CHART
 * (allergies, feeding, medications, behavior) prints on EVERY page, each in
 * its own headed section, because the kennel reads whichever page is on
 * the run door. Bed and locker print big in the header of every page too
 * (Sawyer 06, B5). The belongings / this-stay / notes blocks print on the
 * LAST page only.
 *
 * Black-and-white is the design constraint, not an afterthought (Ted: "it
 * still prints clean in black and white"). Colour NEVER carries meaning on
 * its own here: every section has a written caps label, and the two
 * "danger" states (an allergy, a medication) add a heavy solid border, a
 * thick left bar and a glyph that survive a greyscale laser printer. The
 * "None on file" states are a thin DASHED border and regular weight, so
 * "nothing" and "something" differ in shape and weight before they differ
 * in colour.
 */
export function RunCard({ data }: RunCardProps): JSX.Element {
  const columns = buildStayColumns(data.stayStartISO, data.stayEndISO);
  const pages = paginateColumns(columns, 7);
  const rows = buildGridRows(data.medications);
  // #716 — the per-stay override is what a human said about THIS stay;
  // formatFeeding summarises the owner's standing profile. When both exist
  // the stay wins, because it is the more recent and more specific fact.
  const override = nonEmpty(data.feedingOverride);
  const feedingSummary = override ?? formatFeeding(data.feeding);
  const belongings = data.belongings ?? [];
  const hasBelongings = belongings.length > 0;
  const verifiedLabel = formatStampDate(data.careVerifiedAtISO);
  const medNotes = nonEmpty(data.medicationNotesText);
  const hasMedications = data.medications.length > 0 || medNotes !== null;
  const allergies = formatAllergies(data.allergies);
  const behaviorNotes = nonEmpty(data.behaviorNotes);
  const temperament = nonEmpty(data.temperament);
  const overridden = new Set(data.stayEdits?.overridden ?? []);
  const stayNote = nonEmpty(data.stayEdits?.note);
  const stayFields = (data.stayEdits?.fields ?? []).filter(
    (f) => f.label.trim().length > 0,
  );
  const hasStayEdits = stayNote !== null || stayFields.length > 0;
  const record = data.visitRecord ?? null;
  const printedLabel = record ? formatStampDate(record.printedAtISO) : null;
  // #597 / B7 — caption only the parts of the card that really print Gingr
  // text: the med note when one is shown, the profile feeding note unless a
  // check-in override replaced it.
  const feedingNoteShown = override === null && nonEmpty(data.feeding.notes) !== null;
  const gingrCaption = formatGingrImportCaption({
    medication: medNotes ? data.gingrCare?.medicationImportedAtISO : null,
    feeding: feedingNoteShown ? data.gingrCare?.feedingImportedAtISO : null,
  });

  const age = formatAge(data.birthDateISO);
  const sexAltered = formatSexAltered(data.sex, data.alteredStatus, data.isNeutered);
  const stayWindow = formatStayWindow({
    startISO: data.stayStartISO,
    endISO: data.stayEndISO,
    dropOffTime: data.dropOffTime,
    pickUpTime: data.pickUpTime,
  });
  // breed · age · sex/altered — the Gingr identity line, empties dropped.
  const identityBits = [data.breed, age, sexAltered].filter(
    (b): b is string => typeof b === 'string' && b.trim().length > 0,
  );

  return (
    <div
      className="ferro-run-card-doc"
      data-testid={`run-card-${data.cardId}`}
      data-dog-name={data.dogName}
      data-source={record ? 'visit_record' : 'live'}
    >
      {pages.map((pageColumns, pageIdx) => {
        const isLastPage = pageIdx === pages.length - 1;
        return (
          <article className="ferro-run-card" key={`page-${pageIdx}`}>
            <header className="ferro-run-card__header">
              <div className="ferro-run-card__photo">
                {data.photoUrl ? (
                  <img src={data.photoUrl} alt={data.dogName} />
                ) : (
                  <div className="ferro-run-card__photo--placeholder" aria-hidden="true">
                    {initial(data.dogName)}
                  </div>
                )}
              </div>
              <div className="ferro-run-card__heading">
                <h2 className="ferro-run-card__name">{data.dogName}</h2>
                {/* Ted #28 Phase 2 — the flags that follow the dog, on
                    EVERY page: the kennel reads page 1 of a ten-day stay
                    too. */}
                {data.flags && data.flags.length > 0 ? (
                  <div className="ferro-run-card__flags" data-testid={`run-card-flags-${data.cardId}`}>
                    <DogFlagChips flags={data.flags} scope="dog" testIdPrefix={`run-card-${data.cardId}-`} />
                  </div>
                ) : null}
                <div className="ferro-run-card__service">
                  {data.serviceLabel}
                  {data.dogCount > 1 ? `, Dog ${data.dogIndex}` : ''}
                  {pages.length > 1 ? (
                    <span className="ferro-run-card__page-of">
                      {' '}
                      · Run Card {pageIdx + 1} of {pages.length}
                    </span>
                  ) : null}
                </div>
                <ul className="ferro-run-card__facts">
                  {identityBits.length > 0 ? (
                    <li>{identityBits.join(', ')}</li>
                  ) : null}
                  <li>
                    {data.ownerName}
                    {data.ownerPhone ? (
                      <span className="ferro-run-card__owner-phone"> {data.ownerPhone}</span>
                    ) : null}
                  </li>
                </ul>
                <div className="ferro-run-card__stay">
                  <strong>{data.serviceLabel}:</strong> {stayWindow}
                </div>
              </div>
              {/* Sawyer 06 beds-lockers-runcard (B5) — bed and locker, big, on EVERY page. */}
              <div className="ferro-run-card__placement-top" data-testid={`run-card-placement-${data.cardId}`}>
                <PlacementBox label="BED" value={data.placement?.bedName ?? null} testId={`run-card-bed-${data.cardId}`} />
                <PlacementBox label="LOCKER" value={data.placement?.locker ?? null} testId={`run-card-locker-${data.cardId}`} />
              </div>
            </header>

            {/* ── THE SAFETY CHART — every page ──────────────────────── */}
            <div className="ferro-run-card__chart">
              {/* Ted Item 35 → #1217 — allergies ALWAYS have their own
                  section. Present: heavy solid border + thick bar + ⚠ +
                  bold caps, so it reads as a warning with no colour at all.
                  None: a thin dashed box that says so in words. The red
                  block therefore only ever means "this dog has one". */}
              {allergies ? (
                <section
                  className="ferro-run-card__allergies"
                  data-testid={`run-card-allergies-${data.cardId}`}
                  role="note"
                  aria-label={`Allergies: ${allergies}`}
                >
                  <span className="ferro-run-card__allergies-label">⚠ ALLERGIES:</span>{' '}
                  <span className="ferro-run-card__allergies-text">
                    {allergies}
                    {overridden.has('allergies') ? <StayTag /> : null}
                  </span>
                </section>
              ) : (
                <section
                  className="ferro-run-card__allergies-none"
                  data-testid={`run-card-allergies-none-${data.cardId}`}
                  aria-label="Allergies: none on file"
                >
                  <span className="ferro-run-card__sect-label">ALLERGIES:</span>{' '}
                  <span className="ferro-run-card__none-text">None on file</span>
                </section>
              )}

              <div className="ferro-run-card__chart-row">
                <section
                  className="ferro-run-card__sect ferro-run-card__sect--feeding"
                  data-testid={`run-card-feeding-${data.cardId}`}
                  aria-label="Feeding"
                >
                  <h3 className="ferro-run-card__sect-head">FEEDING</h3>
                  <div className="ferro-run-card__sect-body">
                    {feedingSummary ? (
                      <>
                        {feedingSummary}
                        {override ? <StayTag /> : null}
                      </>
                    ) : (
                      <span className="ferro-run-card__none-text">
                        None on file — ask owner at drop-off
                      </span>
                    )}
                  </div>
                </section>

                {/* Medications on every page (was a one-line strip, #717,
                    plus a last-page block): a ten-day stay's page 1 is what
                    hangs on the run tonight. No meds is said in words, in
                    the quiet dashed style — never the red treatment. */}
                {hasMedications ? (
                  <section
                    className="ferro-run-card__sect ferro-run-card__sect--meds"
                    data-testid={`run-card-medication-flag-${data.cardId}`}
                    aria-label="Medications"
                  >
                    <h3 className="ferro-run-card__sect-head">℞ MEDICATIONS</h3>
                    <div className="ferro-run-card__sect-body">
                      {data.medications.length > 0 ? (
                        <ul className="ferro-run-card__med-list">
                          {data.medications.map((m) => (
                            <li key={m.id}>
                              <strong>{m.name}</strong>
                              {/* Sawyer 10 customer-booking-verify-policy (C6) — the word, not colour: legible in greyscale. */}
                              {m.lockbox ? (
                                <span className="ferro-run-card__lockbox-tag" data-testid={`run-card-lockbox-${m.id}`}>
                                  LOCKBOX
                                </span>
                              ) : null}
                              {m.dosage ? ` — ${m.dosage}` : ''}
                              {m.frequency ? ` (${m.frequency.replace(/_/g, ' ')})` : ''}
                              {m.instructions ? ` — ${m.instructions}` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {medNotes ? (
                        <div className="ferro-run-card__med-notes">
                          {medNotes}
                          {overridden.has('medications') ? <StayTag /> : null}
                        </div>
                      ) : null}
                    </div>
                  </section>
                ) : (
                  <section
                    className="ferro-run-card__sect ferro-run-card__sect--none"
                    data-testid={`run-card-meds-none-${data.cardId}`}
                    aria-label="Medications: none on file"
                  >
                    <h3 className="ferro-run-card__sect-head">MEDICATIONS</h3>
                    <div className="ferro-run-card__sect-body">
                      <span className="ferro-run-card__none-text">None on file</span>
                    </div>
                  </section>
                )}
              </div>

              {temperament || behaviorNotes ? (
                <section
                  className="ferro-run-card__sect ferro-run-card__sect--behavior"
                  data-testid={`run-card-behavior-${data.cardId}`}
                  aria-label="Behavior"
                >
                  <h3 className="ferro-run-card__sect-head">BEHAVIOR</h3>
                  <div className="ferro-run-card__sect-body">
                    {temperament ? (
                      <div>
                        <strong>Temperament:</strong> {temperament}
                      </div>
                    ) : null}
                    {behaviorNotes ? (
                      <div className="ferro-run-card__behavior-notes">
                        {behaviorNotes}
                        {overridden.has('behavior') ? <StayTag /> : null}
                      </div>
                    ) : null}
                  </div>
                </section>
              ) : null}

              {gingrCaption ? (
                <div
                  className="ferro-run-card__source"
                  data-testid={`run-card-gingr-source-${data.cardId}`}
                >
                  {gingrCaption}
                </div>
              ) : null}
            </div>

            {/* DAY-GRID — one column per day, rows = each feeding + each
                medication administration. Cells are intentionally empty:
                staff hand-initial each as they feed / medicate. This is the
                PACFA paper administration ledger (#553) — CO requires an
                initialed, timed record until digital sign-off is confirmed
                acceptable. */}
            <div className="ferro-run-card__grid-wrap">
              <table className="ferro-run-card__grid">
                <thead>
                  <tr>
                    <th scope="col" className="ferro-run-card__grid-rowhead" />
                    {pageColumns.map((col) => (
                      <th scope="col" key={col.iso} className="ferro-run-card__grid-day">
                        <div className="ferro-run-card__grid-weekday">{col.weekday}</div>
                        <div className="ferro-run-card__grid-date">{col.dateLabel}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key} data-row-kind={row.kind}>
                      <th scope="row" className="ferro-run-card__grid-rowhead">
                        {row.label}
                      </th>
                      {pageColumns.map((col) => (
                        <td key={col.iso} className="ferro-run-card__grid-cell" />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {isLastPage ? (
              <>
                {/* Ted #46 (#1218) — what the desk added to THIS stay's card
                    at check-in: a note, and any labelled field ("Pickup by",
                    "Locker"). Saved on the stay, so a reprint keeps it. */}
                {hasStayEdits ? (
                  <section
                    className="ferro-run-card__sect ferro-run-card__sect--stay"
                    data-testid={`run-card-stay-edits-${data.cardId}`}
                    aria-label="This stay"
                  >
                    <h3 className="ferro-run-card__sect-head">THIS STAY</h3>
                    <div className="ferro-run-card__sect-body">
                      {stayFields.length > 0 ? (
                        <dl className="ferro-run-card__stay-fields">
                          {stayFields.map((f, i) => (
                            <div key={`${f.label}-${i}`} className="ferro-run-card__stay-field">
                              <dt>{f.label}:</dt>
                              <dd>{f.value}</dd>
                            </div>
                          ))}
                        </dl>
                      ) : null}
                      {stayNote ? (
                        <p className="ferro-run-card__stay-note">{stayNote}</p>
                      ) : null}
                    </div>
                  </section>
                ) : null}

                {/* #716 — DATA-DRIVEN when this dog has belongings on
                    file, the hardcoded prompts otherwise. The two
                    write-in lines print in both states: the locker
                    changes every stay, and a surprise item still needs
                    somewhere to go. */}
                <section
                  className="ferro-run-card__belongings"
                  data-testid={`run-card-belongings-${data.cardId}`}
                  data-belongings-source={hasBelongings ? 'data' : 'prompts'}
                >
                  <div className="ferro-run-card__section-label">
                    Belongings checked in
                    {verifiedLabel ? (
                      <span className="ferro-run-card__verified">
                        {' '}
                        · verified {verifiedLabel}
                      </span>
                    ) : null}
                  </div>
                  <ul className="ferro-run-card__belongings-list">
                    {(hasBelongings ? belongings : BELONGINGS_PROMPTS).map(
                      (item) => (
                        <li key={item}>
                          <span
                            className="ferro-run-card__checkbox"
                            aria-hidden="true"
                          />{' '}
                          {item}
                        </li>
                      ),
                    )}
                    {WRITE_IN_LINES.map((i) => (
                      <li key={`write-${i}`}>
                        <span className="ferro-run-card__checkbox" aria-hidden="true" />{' '}
                        <span className="ferro-run-card__write-line ferro-run-card__write-line--inline" />
                      </li>
                    ))}
                  </ul>
                </section>

                <section className="ferro-run-card__notes">
                  <div className="ferro-run-card__section-label">Notes</div>
                  <div className="ferro-run-card__write-line" />
                  <div className="ferro-run-card__write-line" />
                </section>
              </>
            ) : null}

            {/* Ted #46 — a reprint of the visit's record says so on paper.
                If today's profile now reads differently, the paper says
                that too: a reprint must never pass for today's truth. */}
            {record && printedLabel ? (
              <footer
                className="ferro-run-card__record"
                data-testid={`run-card-record-${data.cardId}`}
              >
                Visit record · printed {printedLabel}
                {record.changedSincePrint
                  ? ' · the dog’s profile has changed since — check the app'
                  : ''}
              </footer>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
